import { useEffect, useState } from 'react';

const STORAGE_KEY = 'furni.cart.v1';

export interface CartItem {
  quote_id: string;
  qty: number;
  notes?: string;
  added_at: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

// Cached snapshot so useSyncExternalStore sees a stable reference until the storage string actually changes.
const EMPTY: CartItem[] = [];
let cachedRaw: string | null = null;
let cachedItems: CartItem[] = EMPTY;

function notify(): void {
  // Force next read() to re-parse
  cachedRaw = null;
  for (const l of listeners) l();
}

function read(): CartItem[] {
  if (typeof window === 'undefined') return EMPTY;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === cachedRaw) return cachedItems;
  cachedRaw = raw;
  if (!raw) {
    cachedItems = EMPTY;
    return cachedItems;
  }
  try {
    const parsed = JSON.parse(raw);
    cachedItems = Array.isArray(parsed) ? (parsed as CartItem[]) : EMPTY;
  } catch {
    cachedItems = EMPTY;
  }
  return cachedItems;
}

function write(items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  notify();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) notify();
  });
}

export const cart = {
  getItems: read,
  add(item: Omit<CartItem, 'added_at'>): void {
    const items = read();
    const existing = items.findIndex((i) => i.quote_id === item.quote_id);
    if (existing >= 0) {
      items[existing] = {
        ...items[existing],
        ...item,
        added_at: items[existing]!.added_at,
      };
    } else {
      items.push({ ...item, added_at: new Date().toISOString() });
    }
    write(items);
  },
  update(quote_id: string, patch: Partial<Pick<CartItem, 'qty' | 'notes'>>): void {
    const items = read().map((i) => (i.quote_id === quote_id ? { ...i, ...patch } : i));
    write(items);
  },
  remove(quote_id: string): void {
    write(read().filter((i) => i.quote_id !== quote_id));
  },
  clear(): void {
    write([]);
  },
  has(quote_id: string): CartItem | null {
    return read().find((i) => i.quote_id === quote_id) ?? null;
  },
  count(): number {
    return read().reduce((sum, i) => sum + i.qty, 0);
  },
  pieceCount(): number {
    return read().length;
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useCart(): CartItem[] {
  const [items, setItems] = useState<CartItem[]>(() => read());
  useEffect(() => {
    const update = () => setItems(read());
    update();
    return cart.subscribe(update);
  }, []);
  return items;
}

export function quantityDiscountPct(pieceCount: number): number {
  if (pieceCount >= 4) return 15;
  if (pieceCount === 3) return 10;
  if (pieceCount === 2) return 5;
  return 0;
}
